import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Api from "../Service/Api";
import { store } from "../../redux/store";
import {
  setCredentials,
  setUser as setReduxUser,
  clearCredentials,
} from "../../redux/authSlice";

const AccountContext = createContext(null);

export function AccountProvider({ children }) {
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [walletError, setWalletError] = useState(false);
  const revision = useRef(0);
  const activeUserId = useRef(null);
  const accountRead = useRef(0);
  const walletRead = useRef(0);
  const changingAccount = useRef(false);

  const invalidateReads = useCallback(() => {
    revision.current += 1;
    accountRead.current += 1;
    walletRead.current += 1;
  }, []);

  const clearAccount = useCallback(() => {
    invalidateReads();
    activeUserId.current = null;
    setUser(null);
    setWallet(null);
    setWalletError(false);
    setLoading(false);
    try {
      store.dispatch(clearCredentials());
      localStorage.removeItem("token");
      localStorage.removeItem("Auth");
    } catch {
      // Ignore storage errors
    }
  }, [invalidateReads]);

  const refreshWallet = useCallback(async () => {
    if (changingAccount.current || !activeUserId.current) return null;
    const version = revision.current;
    const request = ++walletRead.current;
    try {
      const { data } = await Api.get("/wallet");
      if (version !== revision.current || request !== walletRead.current)
        return null;
      if (data.userId !== activeUserId.current) {
        clearAccount();
        return null;
      }
      setWallet(data);
      setWalletError(false);
      return data;
    } catch (error) {
      if (version !== revision.current || request !== walletRead.current)
        return null;
      setWalletError(true);
      if (error.response?.status === 401) {
        clearAccount();
      }
      throw error;
    }
  }, [clearAccount]);

  const refreshAccount = useCallback(async () => {
    if (changingAccount.current) return;
    const version = revision.current;
    const request = ++accountRead.current;
    try {
      const { data } = await Api.get("/auth/me");
      if (version !== revision.current || request !== accountRead.current)
        return;
      if (activeUserId.current !== data.user.id) {
        revision.current += 1;
        walletRead.current += 1;
        setWallet(null);
        setWalletError(false);
      }
      activeUserId.current = data.user.id;
      setUser((previous) =>
        previous &&
        previous.id === data.user.id &&
        previous.name === data.user.name &&
        previous.email === data.user.email &&
        previous.role === data.user.role
          ? previous
          : data.user
      );
      try {
        store.dispatch(setReduxUser(data.user));
      } catch {
        // Ignore store errors
      }
      await refreshWallet();
    } catch (error) {
      if (
        request === accountRead.current &&
        !changingAccount.current &&
        error.response?.status === 401
      ) {
        clearAccount();
      }
    } finally {
      if (request === accountRead.current && !changingAccount.current)
        setLoading(false);
    }
  }, [refreshWallet, clearAccount]);

  useEffect(() => {
    refreshAccount();
    window.addEventListener("focus", refreshAccount);
    return () => {
      invalidateReads();
      window.removeEventListener("focus", refreshAccount);
    };
  }, [refreshAccount, invalidateReads]);

  useEffect(() => {
    if (!user) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible")
        refreshWallet().catch(() => {});
    }, 30000);
    return () => clearInterval(timer);
  }, [user, refreshWallet]);

  const signin = async (email, password) => {
    if (changingAccount.current)
      throw new Error("An account change is already in progress.");
    changingAccount.current = true;
    invalidateReads();
    setWallet(null);
    setWalletError(false);
    try {
      const { data } = await Api.post("/auth/signin", { email, password });
      activeUserId.current = data.user.id;
      setUser(data.user);
      const accessToken =
        data?.accessToken ||
        data?.token ||
        data?.session ||
        data?.data?.accessToken ||
        data?.data?.token;
      try {
        store.dispatch(
          setCredentials({
            user: data.user,
            accessToken: accessToken || null,
          })
        );
        if (accessToken) {
          localStorage.setItem("token", accessToken);
          localStorage.setItem("Auth", "true");
        }
      } catch {
        // Ignore storage errors
      }
    } finally {
      invalidateReads();
      changingAccount.current = false;
      setLoading(false);
    }
    await refreshWallet().catch(() => {});
  };
  const signout = async () => {
    if (changingAccount.current)
      throw new Error("An account change is already in progress.");
    changingAccount.current = true;
    invalidateReads();
    try {
      await Api.post("/auth/signout", {});
      clearAccount();
    } finally {
      invalidateReads();
      changingAccount.current = false;
      setLoading(false);
    }
  };
  return (
    <AccountContext.Provider
      value={{
        user,
        wallet,
        loading,
        walletError,
        signin,
        signout,
        clearAccount,
        refreshWallet,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount() {
  const context = useContext(AccountContext);
  if (!context) throw new Error("AccountProvider is required");
  return context;
}
